<script>
  // Imports
  import { onDestroy } from 'svelte';

  // Parent callbacks (Svelte 5 style: events are just props)
  let {
    onImageCaptured = () => {},
    onImageDiscarded = () => {},
    onImageAccepted = () => {}
  } = $props();

  // State for managing camera component
  let stream = $state(null);
  let videoRef = $state();
  let canvasRef;
  let error = $state('');
  let capturedImage = $state(null);
  let cameraFacingMode = $state('user'); // 'user' for front camera, 'environment' for back camera
  let cameraActive = $state(false);
  
  // Initialize camera stream
  async function startCamera() {
    try {
      // Reset any previous error
      error = '';
      
      // Request camera access with specified constraints
      const constraints = {
        video: { 
          facingMode: cameraFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Set the stream as the source for the video
      if (videoRef) {
        videoRef.srcObject = stream;
        cameraActive = true;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      error = `Could not access camera: ${err.message}`;
      cameraActive = false;
    }
  }
  
  // Stop camera stream
  function stopCamera() {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      stream = null;
      cameraActive = false;
      
      if (videoRef) {
        videoRef.srcObject = null;
      }
    }
  }
  
  // Toggle between front and back camera
  async function switchCamera() {
    // Stop the current stream before switching
    stopCamera();
    
    // Toggle the facing mode
    cameraFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';
    
    // Restart camera with new facing mode
    await startCamera();
  }
  
  // Take photo from camera stream
  function capturePhoto() {
    if (!videoRef || !canvasRef || !stream) {
      error = 'Camera not ready';
      return;
    }
    
    try {
      const video = videoRef;
      const canvas = canvasRef;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw the current video frame to the canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert to a data URL (base64 encoded string)
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      // Create a Blob from data URL
      const blobData = dataURItoBlob(imageDataUrl);
      
      // Create a File object from the Blob
      const fileName = `photo_${new Date().toISOString()}.jpg`;
      const fileObject = new File([blobData], fileName, { type: 'image/jpeg' });
      
      // Update state and dispatch event
      capturedImage = {
        url: imageDataUrl,
        file: fileObject
      };
      
      // Notify parent component about new image
      onImageCaptured(capturedImage);
      
    } catch (err) {
      console.error('Error capturing photo:', err);
      error = `Error capturing photo: ${err.message}`;
    }
  }
  
  // Convert base64 data URI to a Blob object
  function dataURItoBlob(dataURI) {
    // Convert base64 to raw binary data held in a string
    const byteString = atob(dataURI.split(',')[1]);
    
    // Get the MIME type of the image
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    
    // Create an array buffer and write the bytes of the string to it
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    // Create a Blob with the array buffer
    return new Blob([ab], { type: mimeString });
  }
  
  // Discard the captured image
  function discardImage() {
    capturedImage = null;
    // Notify parent that image was discarded
    onImageDiscarded();
  }
  
  // Accept the captured image
  function acceptImage() {
    // Notify parent that image was accepted
    onImageAccepted(capturedImage);
    // Stop camera to save battery/resources
    stopCamera();
  }
  
  // Clean up when component is destroyed
  onDestroy(() => {
    stopCamera();
  });
</script>

<div class="camera-capture bg-white rounded-lg shadow-md p-4">
  {#if error}
    <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
      <p>{error}</p>
      {#if error.includes('Could not access camera')}
        <p class="mt-2 text-sm">
          Please make sure you have granted camera permission to this site.
          You may need to check your browser settings.
        </p>
      {/if}
    </div>
  {/if}
  
  {#if !capturedImage}
    <!-- Camera Preview -->
    <div class="relative">
      <video 
        bind:this={videoRef}
        autoplay 
        playsinline 
        muted 
        class="w-full h-full rounded bg-gray-200 aspect-video object-cover"
        onloadedmetadata={() => videoRef.play()}
      ></video>
      
      <!-- Camera controls -->
      <div class="absolute bottom-4 inset-x-0 flex justify-center space-x-4">
        {#if !cameraActive}
          <button 
            type="button"
            class="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-full shadow-md"
            onclick={startCamera}
          >
            Start Camera
          </button>
        {:else}
          <button 
            type="button"
            class="bg-red-500 hover:bg-red-600 text-white p-3 rounded-full shadow-md" 
            onclick={stopCamera}
            title="Stop Camera"
            aria-label="Stop camera"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <button 
            type="button"
            class="bg-green-500 hover:bg-green-600 text-white p-3 rounded-full shadow-md" 
            onclick={capturePhoto}
            title="Take Photo"
            aria-label="Take photo"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <circle cx="12" cy="12" r="6" fill="currentColor" />
            </svg>
          </button>
          
          <button 
            type="button"
            class="bg-brand-600 hover:bg-brand-700 text-white p-3 rounded-full shadow-md" 
            onclick={switchCamera}
            title="Switch Camera"
            aria-label="Switch camera"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </button>
        {/if}
      </div>
    </div>
  {:else}
    <!-- Image Preview -->
    <div class="relative">
      <img 
        src={capturedImage.url} 
        alt="Captured" 
        class="w-full h-full rounded bg-gray-200 aspect-video object-cover"
      />
      
      <!-- Image controls -->
      <div class="absolute bottom-4 inset-x-0 flex justify-center space-x-4">
        <button 
          type="button"
          class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full shadow-md"
          onclick={discardImage}
        >
          Discard
        </button>
        
        <button 
          type="button"
          class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full shadow-md"
          onclick={acceptImage}
        >
          Use Photo
        </button>
      </div>
    </div>
  {/if}
  
  <!-- Hidden canvas for processing the image -->
  <canvas bind:this={canvasRef} class="hidden"></canvas>
</div> 